import { motion } from "framer-motion";
import { Plus, X } from "lucide-react";
import React, { useState } from "react";
import { generateId, EmojiPicker } from "@repo/core";
import { AppCard, KpiCard, KpiIcons, ProgressBar, SectionTitle } from "../components/ui/AppCard";
import { useCurrency } from "../hooks/useCurrency";
import { useSinkingFunds } from "../hooks/useBudgetData";
import { useT } from "../i18n/useT";
import { computeSinkingFundStats } from "../lib/calculations";
import { db } from "../lib/db";
import { handleFormSubmit, onEnterToSubmitKeyDown } from "../lib/formKeyboard";
import type { SinkingFund } from "../lib/schema";

const PALETTE = ["mint","sky","coral","lemon","lavender"] as const;
type PaletteKey = typeof PALETTE[number];

const PALETTE_CLASSES: Record<PaletteKey, string> = {
  mint:     "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-100",
  sky:      "bg-sky-50     border-sky-200     text-sky-900     dark:bg-sky-900/20     dark:border-sky-800     dark:text-sky-100",
  coral:    "bg-rose-50    border-rose-200    text-rose-900    dark:bg-rose-900/20    dark:border-rose-800    dark:text-rose-100",
  lemon:    "bg-amber-50   border-amber-200   text-amber-900   dark:bg-amber-900/20   dark:border-amber-800   dark:text-amber-100",
  lavender: "bg-violet-50  border-violet-200  text-violet-900  dark:bg-violet-900/20  dark:border-violet-800  dark:text-violet-100",
};

function FundCard({ fund }: { fund: SinkingFund }) {
  const { format } = useCurrency();
  const { t, locale } = useT();
  const { pct, remaining, monthsLeft: months, monthlyNeeded } = computeSinkingFundStats(fund);
  const displayPct = Math.min(100, pct);
  const paletteClass  = PALETTE_CLASSES[(fund.color as PaletteKey) ?? "mint"] ?? PALETTE_CLASSES.mint;

  const handleContribute = async () => {
    const raw = prompt(
      t("sf.contributePrompt", { name: fund.name, current: format(fund.currentSaved) })
    );
    if (!raw) return;
    const add = parseFloat(raw);
    if (isNaN(add) || add <= 0) return;
    await db.sinkingFunds.update(fund.id, { currentSaved: Math.min(fund.targetAmount, fund.currentSaved + add) });
  };

  const handleDelete = async () => {
    if (confirm(t("sf.deleteConfirm", { name: fund.name }))) await db.sinkingFunds.delete(fund.id);
  };

  const dueDate = new Date(fund.targetDate).toLocaleDateString(locale, {
    month: "short",
    year: "numeric",
  });

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <AppCard padding="none" className="overflow-hidden">
        {/* Header */}
        <div className={`px-4 py-3 border-b border-border flex items-center justify-between ${paletteClass}`}>
          <div className="flex items-center gap-2">
            <span className="text-xl leading-none">{fund.icon ?? "🎯"}</span>
            <div>
              <p className="font-semibold text-sm">{fund.name}</p>
              <p className="text-[10px] opacity-60">
                {t("sf.due", { date: dueDate })}
              </p>
            </div>
          </div>
          <button onClick={handleDelete} className="p-1 rounded hover:bg-black/10 transition-colors opacity-60 hover:opacity-100">
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3">
          <div className="flex justify-between items-end">
            <div>
              <p className="app-section-title mb-0.5">{t("sf.saved")}</p>
              <p className="text-xl font-bold text-foreground">{format(fund.currentSaved)}</p>
            </div>
            <div className="text-right">
              <p className="app-section-title mb-0.5">{t("sf.target")}</p>
              <p className="text-xl font-bold text-foreground">{format(fund.targetAmount)}</p>
            </div>
          </div>

          <ProgressBar value={displayPct} showPct height={10} />

          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border text-center">
            {[
              { label: t("sf.remaining"),  value: format(remaining, true) },
              { label: t("sf.monthsLeft"), value: String(months) },
              { label: t("sf.needMonth"), value: format(monthlyNeeded, true) },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
                <p className="text-sm font-semibold text-foreground">{value}</p>
              </div>
            ))}
          </div>

          <button onClick={handleContribute} className="w-full app-btn-outline text-xs py-2">
            {t("sf.contribute")}
          </button>
        </div>
      </AppCard>
    </motion.div>
  );
}

export function SinkingFundsPage() {
  const { format, symbol } = useCurrency();
  const { t } = useT();
  const funds = useSinkingFunds();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name:         "",
    icon:         "🎯",
    targetAmount: "",
    targetDate:   "",
    currentSaved: "0",
    color:        "mint" as PaletteKey,
  });

  const totalTarget = funds.reduce((s, f) => s + f.targetAmount, 0);
  const totalSaved  = funds.reduce((s, f) => s + f.currentSaved,  0);

  const handleAdd = async () => {
    if (!form.name || !form.targetAmount || !form.targetDate) return;
    await db.sinkingFunds.add({
      id:           generateId(),
      name:         form.name,
      icon:         form.icon,
      targetAmount: parseFloat(form.targetAmount),
      targetDate:   form.targetDate,
      currentSaved: parseFloat(form.currentSaved) || 0,
      color:        form.color,
    });
    setShowForm(false);
    setForm({ name: "", icon: "🎯", targetAmount: "", targetDate: "", currentSaved: "0", color: "mint" });
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 w-full">
      <SectionTitle
        action={
          <button onClick={() => setShowForm((v) => !v)} className="app-btn">
            <Plus size={13} /> {t("sf.addFund")}
          </button>
        }
      >
        {t("sf.title")}
      </SectionTitle>

      {/* Total progress */}
      <KpiCard
        label={t("sf.totalSaved")}
        value={format(totalSaved)}
        sub={`/ ${format(totalTarget)} · ${((totalSaved / Math.max(totalTarget, 1)) * 100).toFixed(0)}%`}
        color="mint"
        icon={<KpiIcons.savings size={16} />}
        index={0}
      >
        <ProgressBar value={(totalSaved / Math.max(totalTarget, 1)) * 100} showPct={false} height={8} />
      </KpiCard>

      {/* Add form */}
      {showForm && (
        <AppCard color="lemon" padding="md">
          <form
            onKeyDown={onEnterToSubmitKeyDown}
            onSubmit={(e) => handleFormSubmit(e, () => handleAdd())}
          >
          <p className="font-semibold text-sm mb-3">{t("sf.newTitle")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">{t("sf.name")}</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="app-input"
                placeholder={t("sf.namePh")}
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">{t("sf.icon")}</label>
              <EmojiPicker
                value={form.icon}
                onChange={(emoji) => setForm((f) => ({ ...f, icon: emoji }))}
                className="w-full justify-between"
                aria-label={t("sf.fundIcon")}
              />
            </div>
            {[
              { label: `${t("sf.targetAmount")} (${symbol})`, field: "targetAmount", type: "number" },
              { label: t("sf.targetDate"),                    field: "targetDate",   type: "date"   },
              { label: `${t("sf.alreadySaved")} (${symbol})`, field: "currentSaved", type: "number" },
            ].map(({ label, field, type }) => (
              <div key={field}>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">{label}</label>
                <input
                  type={type}
                  value={(form as Record<string, string>)[field]}
                  onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                  className="app-input"
                />
              </div>
            ))}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">{t("sf.color")}</label>
              <select value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value as PaletteKey }))} className="app-input">
                {PALETTE.map((c) => (
                  <option key={c} value={c}>{t(`palette.${c}`)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button type="submit" className="app-btn">{t("sf.addFund")}</button>
            <button type="button" onClick={() => setShowForm(false)} className="app-btn-outline">{t("common.cancel")}</button>
          </div>
          </form>
        </AppCard>
      )}

      {/* Fund grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {funds.map((f) => <FundCard key={f.id} fund={f} />)}
        {funds.length === 0 && (
          <p className="col-span-2 text-center text-sm text-muted-foreground py-8">
            {t("sf.empty")}
          </p>
        )}
      </div>
    </div>
  );
}
